import * as React from 'react';
import classnames from 'classnames';

import { withContextMenu } from 'components/context-menu/ContextMenuHOC';
import MoreButton from 'components/buttons/more-button/MoreButton';
import ImageUploader, { MEDIA_TYPES_VIDEOS } from 'components/ImageUploader';

import VideoFromFile from './components/VideoFromFile';
import VideoWithGallery from './components/VideoWithGallery';

import { __t, applyTranslations } from 'utils/language';
import { isBlobUrl } from 'utils/url';
import { insertIf } from 'utils/list';

import type { Video } from './types/Video.type';

import './realm-outfit-video.scss';
import ActionLink from 'components/buttons/action-link/ActionLink';

applyTranslations(require('./realm-outfit-video.i18n.json'));

interface OwnProps {
  isEditMode?: boolean,
  video?: Video | null,
  isInModal?: boolean,
  hasProcessingCompleted?: boolean,
  onAddOutfitVideo?: (files: Array<File>) => void,
  onRetriggerVideoProcessing?: () => void,
  onDeleteVideo?: () => void,
  onDownloadVideo?: (url: string) => void
}

interface DispatchProps {
}

type Props = DispatchProps & OwnProps;

const MoreButtonWithMenu = withContextMenu(MoreButton, {
  position: 'bottom'
});

const RealmOutfitVideo = ({
  video,
  isEditMode,
  isInModal,
  hasProcessingCompleted = true,
  onAddOutfitVideo,
  onDeleteVideo,
  onDownloadVideo,
  onRetriggerVideoProcessing }: Props): JSX.Element => {
  const isFile = video?.url && isBlobUrl(video.url);
  const uploadRef = React.useRef<HTMLInputElement>();

  const menuClasses = classnames('realm-outfit-video__menu', {
    'realm-outfit-video__menu--with-new-tab': !isFile
  });

  const items = [
    ...insertIf(onDeleteVideo, {
      icon: 'trash-custom-fg',
      text: __t('realm_outfit_video.delete'),
      onClick: onDeleteVideo
    }),
    ...insertIf(!isFile && onDownloadVideo && onDeleteVideo, {
      type: 'separator'
    }),
    ...insertIf(!isFile && onDownloadVideo, {
      icon: 'download-custom-fg',
      text: __t('realm_outfit_video.download'),
      onClick: () => {
        if (onDownloadVideo && video) {
          onDownloadVideo(video.url);
        }
      }
    }),
    ...insertIf(!isFile && onDownloadVideo && onDeleteVideo, {
      type: 'separator'
    }),
    ...insertIf(isFile, {
      icon: 'add-or-delete-custom-fg',
      text: __t('realm_outfit_video.replace_content'),
      onClick: () => {
        if (uploadRef.current) {
          uploadRef.current.click();
        }
      }
    })
  ];

  if (!video && !isEditMode) {
    return (
      <div className="realm-outfit-video__empty">{__t('realm_outfit_video.empty_video')}</div>
    );
  }

  return (
    <div className="realm-outfit-video">
      {
        onRetriggerVideoProcessing && hasProcessingCompleted && (
          <ActionLink
            color="secondary"
            weight="bold"
            onClick={onRetriggerVideoProcessing}
          >
            {__t('realm_outfit_video.retrigger_video_processing')}
          </ActionLink>
        )
      }
      {
        !hasProcessingCompleted && (
          <div className="realm-outfit-video__link-text">{__t('realm_outfit_video.video_processing')}</div>
        )
      }
      {
        isEditMode && (
          <ImageUploader
            multiple
            mediaTypes={MEDIA_TYPES_VIDEOS}
            forwardedRef={uploadRef}
            hasIcon
            onUpload={files => {
              if (onAddOutfitVideo) {
                onAddOutfitVideo(files);
              }
            }}
            button={{
              label: __t('realm_outfit_video.upload_video'),
              type: 'button',
              color: 'secondary'
            }}
          />
        )
      }
      {
        video && (
          <div className="realm-outfit-video__main">
            {(isFile || isInModal) ? (
              <VideoFromFile url={video.url} />
            ) : (
              <VideoWithGallery video={video} />
            )}
            <div
              className={menuClasses}
              onClick={e => e.stopPropagation()}
            >
              {isFile ? null : (
                <a
                  rel="noopener noreferrer"
                  target="_blank"
                  href={video.url}
                >
                  <span className="realm-outfit-video__link-text text-overflow-ellipsis">
                    {__t('realm_outfit_video.new_tab')}
                  </span>
                </a>
              )}
              <MoreButtonWithMenu
                width={20}
                contextMenuProps={{
                  items,
                  closeOnScroll: true
                }}
              />
            </div>
          </div>
        )
      }

    </div>
  );
};


export default RealmOutfitVideo;
